import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCcw, Plus, Trash2, KeyRound, UserPlus, ShieldCheck, ShieldOff, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { listUsers, createUser, updateUser, deleteUser, changeOwnPassword } from '../../lib/api';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const Modal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
};

const UtentiTab = ({ currentUsername }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showReset, setShowReset] = useState(null); // target user

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listUsers();
      setUsers(data.items || []);
    } catch {
      toast.error('Impossibile caricare gli utenti');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u) => {
    try {
      await updateUser(u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? 'Utente disattivato' : 'Utente attivato');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Eliminare l'utente "${u.username}"? L'operazione è irreversibile.`)) return;
    try {
      await deleteUser(u.id);
      toast.success('Utente eliminato');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-slate-500">
          {users.length} utent{users.length === 1 ? 'e' : 'i'} totali
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowChangePassword(true)}>
            <KeyRound className="w-4 h-4 mr-2" /> Cambia la mia password
          </Button>
          <Button variant="outline" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCcw className="w-4 h-4 mr-2" />}
            Aggiorna
          </Button>
          <Button onClick={() => setShowCreate(true)} className="bg-sky-600 hover:bg-sky-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Nuovo utente
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Username</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Stato</th>
                <th className="text-left px-4 py-3 font-medium">Creato</th>
                <th className="text-left px-4 py-3 font-medium">Ultimo accesso</th>
                <th className="text-right px-4 py-3 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Caricamento...
                </td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-slate-500">Nessun utente.</td></tr>
              )}
              {!loading && users.map((u) => {
                const isMe = u.username === currentUsername;
                return (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-medium">
                      {u.username}
                      {isMe && <span className="ml-2 text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full">tu</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{u.email || '—'}</td>
                    <td className="px-4 py-3">
                      {u.is_active
                        ? <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">Attivo</span>
                        : <span className="text-xs px-2 py-1 rounded-full bg-slate-200 text-slate-600">Disattivato</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatDate(u.last_login_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setShowReset(u)}
                          className="text-slate-400 hover:text-sky-600 p-1"
                          title="Reset password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleActive(u)}
                          disabled={isMe}
                          className="text-slate-400 hover:text-amber-600 p-1 disabled:opacity-40 disabled:cursor-not-allowed"
                          title={u.is_active ? 'Disattiva' : 'Attiva'}
                        >
                          {u.is_active ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleDelete(u)}
                          disabled={isMe}
                          className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Elimina"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <CreateUserModal open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
      <ChangePasswordModal open={showChangePassword} onClose={() => setShowChangePassword(false)} />
      <ResetPasswordModal user={showReset} onClose={() => setShowReset(null)} onDone={load} />
    </>
  );
};

const CreateUserModal = ({ open, onClose, onCreated }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setUsername(''); setEmail(''); setPassword(''); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createUser({ username, password, email: email || undefined });
      toast.success('Utente creato');
      onClose();
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore creazione utente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Nuovo utente">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Username</Label>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} required minLength={3} pattern="[A-Za-z0-9._-]+" />
          <p className="text-xs text-slate-500 mt-1">Lettere, numeri, punto, underscore o trattino (min 3 caratteri).</p>
        </div>
        <div>
          <Label>Email (opzionale)</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <Label>Password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
          <p className="text-xs text-slate-500 mt-1">Minimo 8 caratteri.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          <Button type="submit" disabled={loading} className="bg-sky-600 hover:bg-sky-700 text-white">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Crea
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const ChangePasswordModal = ({ open, onClose }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setCurrent(''); setNext(''); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await changeOwnPassword(current, next);
      toast.success('Password aggiornata');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Cambia la tua password">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Password attuale</Label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required />
        </div>
        <div>
          <Label>Nuova password</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          <Button type="submit" disabled={loading} className="bg-sky-600 hover:bg-sky-700 text-white">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Aggiorna
          </Button>
        </div>
      </form>
    </Modal>
  );
};

const ResetPasswordModal = ({ user, onClose, onDone }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) setPassword(''); }, [user]);

  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateUser(user.id, { password });
      toast.success(`Password di ${user.username} aggiornata`);
      onClose();
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={!!user} onClose={onClose} title={`Reset password — ${user.username}`}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>Nuova password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoFocus />
          <p className="text-xs text-slate-500 mt-1">Minimo 8 caratteri. L'utente dovrà usare questa nuova password al prossimo accesso.</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          <Button type="submit" disabled={loading} className="bg-sky-600 hover:bg-sky-700 text-white">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Salva
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UtentiTab;
